import { serverSideTranslations } from "next-i18next/serverSideTranslations";

import BreadCrumb from "@/components/global/BreadCrumb";
import { modifyMenuTreeData } from "@/helpers/category";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "next-i18next";
import useScreenSize from "@/hooks/display/useDisplay";
import Link from "next/link";
import { getCategoryTree } from "@/services/category";
import { fetchHandshake } from "@/services/handshake";
import { useRouter } from "next/router";
import NextImage from "@/components/global/NextImage";
import { validateImgSrc } from "@/utils/global";

const Index = ({ categoryTree }) => {
  const { t } = useTranslation("common");

  const { isResponsive } = useScreenSize();

  const parentCategories = useMemo(
    () =>
      modifyMenuTreeData(categoryTree).filter(
        (cat) => cat?.children_data?.length > 0
      ),
    [categoryTree]
  );

  const [catChildren, setCatChildren] = useState([]);
  const [active, setActive] = useState({});

  useEffect(() => {
    if (parentCategories.length) {
      setActive(parentCategories?.[0]);
      setCatChildren(modifyMenuTreeData(parentCategories?.[0]?.children_data));
    }
  }, [parentCategories]);

  const handleImageSrc = (srcImage) => {
    const baseUrl = process.env.NEXT_PUBLIC_IMG_BASEURL;
    return srcImage
      ? `${baseUrl}${
          srcImage?.startsWith("/media/")
            ? srcImage?.replace("/media", "")
            : srcImage
        }`
      : `${baseUrl}${"/catalog/category/undefined.jpg"}`;
  };

  const { locale } = useRouter();
  const BreadCrumbItems = [
    {
      name: locale == "en" ? "Categories" : "التصنيفات",
      url: "/categories",
      active: true,
      key: "categories",
    },
  ];

  return (
    <>
      <BreadCrumb items={BreadCrumbItems} />
      <section className="w-11/12 mx-auto my-4 sm:my-8">
        <h1 className="font-semibold sm:text-xl">{t("Categories")}</h1>
        <ul className="flex items-center gap-3 my-3 overflow-auto sm:my-6">
          {[1, 2, 3, 4, 5, 6].map((i) => (
            <li
              key={i}
              className=" uppercase whitespace-nowrap border py-2 px-6 rounded-md text-secondary-900 text-[9px] min-[460px]:text-base text-center font-bold min-[460px]:font-semibold"
            >
              Top Deals
            </li>
          ))}
        </ul>
        <div className="grid items-start grid-cols-12 gap-4">
          <div className="col-span-4 min-[460px]:col-span-4 md:col-span-3 bg-zinc-100 p-3 h-fit">
            <h2 className="font-bold text-xs min-[460px]:text-lg">
              {t("Just For You")}
            </h2>
            <ul className="flex flex-col gap-2 my-2">
              {parentCategories?.map((cat, i) => (
                <li
                  onClick={() => {
                    setCatChildren(modifyMenuTreeData(cat?.children_data));
                    setActive(cat);
                  }}
                  key={i}
                  className={`text-xs min-[460px]:text-lg cursor-pointer hover:bg-slate-200 py-px px-1 sm:p-1 ${
                    active?.id === cat?.id ? "bg-slate-200" : ""
                  }`}
                >
                  {cat?.name}
                </li>
              ))}
            </ul>
          </div>
          <div className="col-span-8 min-[460px]:col-span-8 md:col-span-9 grid grid-cols-2 min-[370px]:grid-cols-3 lg:grid-cols-4 gap-3">
            {catChildren?.map((subCat, i) => (
              <Link
                href={`${active?.url_key}/${subCat?.url_key}`}
                className="relative w-full overflow-hidden rounded-md"
                key={i}
              >
                <NextImage
                  alt=""
                  className="object-contain w-full h-auto"
                  src={validateImgSrc(
                    isResponsive
                      ? handleImageSrc(subCat?.custom_image_responsive)
                      : handleImageSrc(subCat?.custom_image)
                  )}
                  width={100}
                  height={100}
                />
                <div className="absolute bottom-0 z-40 flex items-end justify-center w-full -translate-x-1/2 left-1/2 h-1/2 bg-gray-top">
                  <span className="uppercase text-center text-white font-normal min-[460px]:font-bold pb-3 text-[9px] min-[460px]:text-base">
                    {subCat?.name}
                  </span>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>
    </>
  );
};

export default Index;

export async function getServerSideProps({ locale, req, res }) {
  // get handshake from cookies or generate new handshake
  const handshake = await fetchHandshake({ req, res, locale });

  const treeResponse = await getCategoryTree(handshake);
  const categoryTree = treeResponse?.data?.data?.children_data || [];

  return {
    props: {
      categoryTree,
      ...(await serverSideTranslations(locale, ["common", "home"])),
    },
  };
}
